// The cabinet's own API calls: the Blazor "Api" HttpClient and its handlers, in the order they wrapped it.
//   LegalGuardHandler    a 451 opens the one legal-acceptance dialog
//   AuthHeaderHandler    the bearer, the language, X-Platform: Web; a 401 refreshes the token once and retries
//   LoggingMessageHandler a failure goes to the client log with its bodies, scrubbed
//   WafTokenCredentialsHandler (http.js)

import type { ApiResponse } from './http.ts';
import { config } from './config.ts';
import { getAccessToken, refreshToken } from './auth.ts';
import { join, send } from './http.ts';
import { log } from './logs.ts';
import { redact } from './redact.ts';
import { emit, state } from './state.ts';

const MAX_LOGGED_BODY = 1500;

/** Set by session.ts: signing out after a refresh was refused. */
let onSessionLost: (() => Promise<void>) | null = null;

export function whenSessionLost(handler: () => Promise<void>): void {
    onSessionLost = handler;
}

const truncate = (value: string): string => (value && value.length > MAX_LOGGED_BODY ? value.slice(0, MAX_LOGGED_BODY) + '...' : value);

interface CabinetOptions {
    method?: string;
    json?: unknown;
    body?: string;
}

function describeBody(options: CabinetOptions): string {
    if (options.json !== undefined) return redact(truncate(JSON.stringify(options.json)));
    if (options.body !== undefined) return options.body ? redact(truncate(options.body)) : '(empty)';

    return '(empty)';
}

/** path is relative to the influencer API. */
export async function cabinetApi(path: string, options: CabinetOptions = {}): Promise<ApiResponse> {
    const url = join(config.influencerApiUrl, path);
    const method = options.method || 'GET';

    // Declared on every request, the legal ones too: the guard and the legal endpoints derive their scope from it,
    // and a scope they disagree on is a dialog that can never be cleared.
    const request = (token: string | null): Promise<ApiResponse> => send(url, {
        ...options,
        method,
        bearer: token,
        language: state.locale,
        headers: { 'X-Platform': 'Web' }
    });

    const startedAt = performance.now();
    const token = getAccessToken();
    let response = await request(token);

    // Only a token that was sent can have expired; an anonymous 401 is the answer, not a lost session.
    if (response.status === 401 && token) {
        if (await refreshToken()) {
            response = await request(getAccessToken());
        } else {
            // Not awaited: signing out unregisters push through this very client, and would wait on itself.
            onSessionLost?.()?.catch?.((error: unknown) => log.error('Sign-out failed', error));
        }
    }

    if (!response.ok) {
        const elapsed = Math.round(performance.now() - startedAt);

        if (response.status === 0) {
            log.error(`HTTP ${method} ${redact(url)} threw after ${elapsed}ms. Request: ${describeBody(options)}`,
                undefined, undefined, 'Lify.Influencer.Handlers.LoggingMessageHandler');
        } else {
            log.warn(`HTTP ${method} ${redact(url)} returned ${response.status} in ${elapsed}ms. `
                + `Request: ${describeBody(options)} Response: ${redact(truncate(response.text)) || '(empty)'}`,
                undefined, 'Lify.Influencer.Handlers.LoggingMessageHandler');
        }
    }

    // The server refused until this person accepts something. The legal endpoints themselves are exempt, and a 451
    // from them must not reopen the dialog that is asking about it.
    if (response.status === 451 && !/legal-documents/i.test(new URL(url).pathname)) {
        const gate = response.json<{ requiredDocuments?: unknown }>();
        const required: unknown[] = Array.isArray(gate?.requiredDocuments) ? gate.requiredDocuments : [];

        if (required.length) {
            emit('legalBlocked', required);
        } else {
            log.error(`Refused with 451 but no documents were named: ${response.text}`, undefined, undefined,
                'Lify.Influencer.Handlers.LegalGuardHandler');
        }
    }

    return response;
}
